import React from "react";
import { Button, Card, Form, Input, Typography, ConfigProvider } from "antd";
import { MailOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { Formik } from "formik";
import * as yup from "yup";
import { useLocation, useNavigate, Link } from "react-router-dom";
import APIService from "../../Service/APIService";
import Swal from "sweetalert2";

const { Title, Text } = Typography;

// Validation Schema for consistency
const forgotPasswordSchema = yup.object().shape({
  Email: yup.string().email("Please enter a valid email").required("Please enter your email"),
});

const ForgotPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const initialEmail = searchParams.get("email") || "";
  const reason = searchParams.get("reason") || "";

  const submitHandler = async (values, { setSubmitting }) => {
    const apiServe = new APIService();
    
    try {
      await apiServe.addDataToBackend(
        { Email: values.Email },
        "forgotpassword"
      );
    } catch (error) {
    } finally {
      Swal.fire({
        title: "Check Your Email",
        text: "If an active account exists for that email, a password reset or account setup link has been sent.",
        icon: "success",
        confirmButtonColor: "#1677ff",
      }).then(() => {
        navigate("/login");
      });
      
      setSubmitting(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Figtree', sans-serif",
          borderRadius: 10,
          colorPrimary: "#1677ff",
        },
      }}
    >
      <div style={backgroundWrapper}>
        <Formik
          initialValues={{ Email: initialEmail }}
          validationSchema={forgotPasswordSchema}
          onSubmit={submitHandler}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <div style={containerStyle}>
              {/* Logo */}
              <div style={{ marginBottom: "32px" }}>
                <img
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  style={{ width: "180px" }}
                  alt="Logo"
                />
              </div>

              <Card bordered={false} style={cardStyle}>
                <div style={{ marginBottom: "24px" }}>
                  <Title level={2} style={{ margin: 0, fontWeight: 500}}>
                    Get a Secure Link
                  </Title>
                  <Text type="secondary" style={{ fontSize: "15px" }}>
                    {reason === "password_expired"
                      ? "Your password has expired. Enter your email address and we'll send you a secure reset link."
                      : "Enter your email address and we'll send you a link to reset your password or finish setting up your account."}
                  </Text>
                </div>

                <Form layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                  <Form.Item
                    label={<span style={labelStyle}>Email Address</span>}
                    validateStatus={touched.Email && errors.Email ? "error" : ""}
                    help={touched.Email && errors.Email}
                  >
                    <Input
                      name="Email"
                      prefix={<MailOutlined style={{ color: "#bfbfbf", marginRight: "4px" }} />}
                      placeholder="name@company.com"
                      size="large"
                      style={inputStyle}
                      value={values.Email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    style={buttonStyle}
                    block
                  >
                    Send Link
                  </Button>

                  <div style={{ textAlign: "center", marginTop: "16px" }}>
                    <Link to="/login" style={backToLoginLink}>
                      <ArrowLeftOutlined style={{ marginRight: "8px" }} />
                      Back to Log In
                    </Link>
                  </div>
                </Form>
              </Card>

             <div style={footerLogoStyle}>
                <Text type="secondary" style={{ fontSize: "12px", marginRight: "8px" }}>
                  POWERED BY
                </Text>
                <img
                  src={`${process.env.PUBLIC_URL}/companyLogo.png`}
                  style={{ width: "120px", filter: "opacity(0.6)" }}
                  alt="Advanced Solution Logo"
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </ConfigProvider>
  );
};

// --- Styles ---

const backgroundWrapper = {
  background: "#EDF3FF",
  minHeight: "100vh",
  fontFamily: "'Figtree', sans-serif",
};

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  padding: "24px",
};

const cardStyle = {
  width: "100%",
  maxWidth: "500px",
  borderRadius: "20px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  padding: "12px",
};

const labelStyle = {
  fontWeight: "600",
  fontSize: "14px",
  color: "#434343",
};

const inputStyle = {
  height: "48px",
  borderRadius: "10px",
};

const buttonStyle = {
  height: "48px",
  borderRadius: "10px",
  fontSize: "16px",
  fontWeight: "600",
  marginTop: "8px",
};

const backToLoginLink = {
  fontSize: "14px",
  fontWeight: "600",
  color: "#64748b", // Slate color for a secondary link
  display: "inline-flex",
  alignItems: "center",
};

const footerLogoStyle = {
  marginTop: "48px",
  display: "flex",
  alignItems: "center",
};

export default ForgotPassword;
